import {
    CallStatuses,
    CallStatusType,
    ActiveCallData,
    FormattedQualifications,
    FormattedTranscriptSpeech,
} from 'contexts/LiveKitContext';
import {
    Room,
    RoomEvent,
    ReconnectContext,
    ConnectionState,
    ConnectionQuality,
    RemoteParticipant,
    RemoteTrack,
    RemoteTrackPublication,
    ParticipantEvent,
    ParticipantKind,
} from 'livekit-client';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useUpdateCallStatus } from 'services/hooks/mutations';
import { CallDetailsResponse } from 'services/api/AgentCenterAPI';
import { useLogging } from 'contexts/LoggingContext';
import { CallType } from 'types/call.type';

type LivekitRoomProps = {
    setShouldCloseCallConsole: (shouldClose: boolean) => void;
    setCallStatus: (status: CallStatusType) => void;
    setTransferTarget: (target: string | null) => void;
    setCallTranscript: (transcript: FormattedTranscriptSpeech[]) => void;
    setCallDetails: (details: CallDetailsResponse['callDetails'] | null) => void;
    setAvailableStatuses: (statuses: CallDetailsResponse['statuses']) => void;
    setPartyInfo: (info: CallDetailsResponse['partyInfo'] | null) => void;
    setQualificationsData: (data: FormattedQualifications[]) => void;
    stopBeeping: () => void;
    activeCallDataRef: React.RefObject<ActiveCallData | null>;
};

export const reconnectPolicy = {
    nextRetryDelayInMs: (context: ReconnectContext) => {
        const { retryCount, elapsedMs, retryReason } = context;
        const errorMessage = retryReason?.message?.toLowerCase() || '';

        // Stop after 12 attempts or 5 minutes
        if (retryCount >= 12 || elapsedMs >= 300000) {
            console.error(`LiveKit giving up after ${retryCount} attempts (${Math.round(elapsedMs / 1000)}s elapsed)`);
            return null;
        }

        // Quick retries for the first 3 attempts (1s, 2s, 4s)
        if (retryCount < 3) return 1000 * Math.pow(2, retryCount);

        // Network/connection errors: more aggressive retries
        if (
            errorMessage.includes('network') ||
            errorMessage.includes('connection') ||
            errorMessage.includes('timeout') ||
            errorMessage.includes('unreachable')
        ) {
            return Math.min(2000 * Math.pow(1.5, retryCount - 3), 30000);
        }

        // Default exponential backoff
        return Math.min(3000 * Math.pow(1.5, retryCount - 3), 60000);
    },
};

export const useLiveKitRoom = ({
    setShouldCloseCallConsole,
    setCallStatus,
    setTransferTarget,
    setCallTranscript,
    setCallDetails,
    setAvailableStatuses,
    setPartyInfo,
    setQualificationsData,
    stopBeeping,
    activeCallDataRef,
}: LivekitRoomProps) => {
    const { info, warn, error: logError } = useLogging();
    const [room, setRoom] = useState<Room | null>(null);
    const livekitRoomRef = useRef<Room | null>(null);

    // Create audio container directly in this hook
    const audioContainerRef = useRef<HTMLDivElement | null>(null);

    const { updateCallStatus } = useUpdateCallStatus();

    const ensureAudioContainer = () => {
        if (!audioContainerRef.current) {
            let container = document.getElementById('remote-audio-container');
            if (!container) {
                container = document.createElement('div');
                container.id = 'remote-audio-container';
                container.style.display = 'none';
                container.style.position = 'absolute';
                container.style.left = '-9999px';
                document.body.appendChild(container);
                console.log('Created audio container in useLiveKitRoom');
            }
            audioContainerRef.current = container as HTMLDivElement;
        }
        return audioContainerRef.current;
    };

    const handleConnectionStateChanged = (state: ConnectionState) => {
        console.log('LiveKit room connection state changed to:', state);
        info('LiveKit room connection state changed to:', { state }, 'useLiveKitRoom', 'connection_state_changed');
    };

    const handleParticipantDisconnected = (participant: RemoteParticipant) => {
        console.log('Participant disconnected:', participant.identity);
        warn('Participant disconnected:', { participant }, 'useLiveKitRoom', 'participant_disconnected');
        if (participant.kind === ParticipantKind.SIP && participant.identity?.includes('+')) {
            toast.warning('The customer has disconnected from the call.');

            setCallStatus(CallStatuses.READY);
            setTransferTarget(null);

            // Reset call related data
            setCallTranscript([]);
            setCallDetails(null);
            setAvailableStatuses([]);
            setPartyInfo(null);
            setQualificationsData([]);

            // clear the room reference
            livekitRoomRef.current = null;
            setRoom(null);
            setShouldCloseCallConsole(true);
        }
    };

    const handleParticipantConnected = async (participant: RemoteParticipant) => {
        console.log('Customer connected:', participant.identity);
        info('Customer connected:', { participant }, 'useLiveKitRoom', 'participant_connected');

        // Handle beeping for customer, need to check for attributes changes to stop beeping
        if (participant.kind === ParticipantKind.SIP && participant.identity?.includes('+')) {
            participant.on(ParticipantEvent.AttributesChanged, (changedAttributes) => {
                if ('sip.callStatus' in changedAttributes) {
                    const callStatus = participant.attributes['sip.callStatus'];

                    switch (callStatus) {
                        case 'dialing':
                            // Call is being dialed
                            break;
                        case 'ringing':
                            // Phone is ringing
                            break;
                        case 'active':
                            // Call is active (picked up)
                            stopBeeping();
                            info(
                                'Beeping stopped in switch case for customer',
                                { participant },
                                'useLiveKitRoom',
                                'beeping_stopped_in_switch_case_for_customer'
                            );
                            break;
                        case 'ended':
                            // Call ended
                            break;
                    }
                }
            });
        }
    };

    const handleDisconnected = async () => {
        stopBeeping();
        console.log('Disconnected from LiveKit room');

        setCallStatus(CallStatuses.READY);
        setTransferTarget(null);

        // Reset call related data
        setCallTranscript([]);
        setCallDetails(null);
        setAvailableStatuses([]);
        setPartyInfo(null);
        setQualificationsData([]);

        // Get call data from ref to avoid closure issues
        const currentCallData = activeCallDataRef.current;
        info('Disconnected from LiveKit room', { currentCallData }, 'useLiveKitRoom', 'disconnected');

        // Update call status if not a manual callback
        // manual callback is handled on voice side so we don't need to update call status here
        if (currentCallData?.callID && !currentCallData.isManualCallback) {
            const response = await updateCallStatus({
                callId: currentCallData.callID,
                partyId: currentCallData.partyID,
                callStatus: CallType.TRANSFER_COMPLETED,
            });
            info(
                'Update call status response in disconnected handler:',
                { response, activeCallID: currentCallData.callID },
                'useLiveKitRoom',
                'update_call_status_response'
            );
        }

        activeCallDataRef.current = null;
        // clear the room reference
        livekitRoomRef.current = null;
        setRoom(null);
        setShouldCloseCallConsole(true);
    };

    const handleTrackSubscribed = (
        track: RemoteTrack,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant
    ) => {
        info(
            'Track subscribed:',
            { track, publication, participant, activeCallID: activeCallDataRef?.current?.callID },
            'useLiveKitRoom',
            'track_subscribed'
        );

        // SAFETY CHECK: Verify participant still exists in room (they could have disconnected during event processing)
        if (!participant.sid) {
            warn(
                'Participant no longer in room, skipping track attachment:',
                { participant, activeCallID: activeCallDataRef?.current?.callID },
                'useLiveKitRoom',
                'participant_no_longer_in_room'
            );
            toast.warning('Participant no longer in room');
            return;
        }

        if (track.kind === 'audio') {
            // Create audio element for remote participant
            const container = ensureAudioContainer();
            const audioElement = track.attach();

            audioElement.autoplay = true;
            audioElement.volume = 1.0;
            audioElement.muted = false;

            // Add to DOM if container exists
            if (container) {
                container.appendChild(audioElement);
                info(
                    'Audio track attached to DOM',
                    { container, audioElement, activeCallID: activeCallDataRef?.current?.callID },
                    'useLiveKitRoom',
                    'audio_track_attached_to_dom'
                );
            } else {
                warn(
                    'Audio container not available to attach track',
                    { container, activeCallID: activeCallDataRef?.current?.callID },
                    'useLiveKitRoom',
                    'audio_container_not_available_to_attach_track'
                );
            }
        }
    };

    const handleTrackUnsubscribed = async (
        track: RemoteTrack,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant
    ) => {
        console.log('Track unsubscribed:', track.kind, 'from participant', participant.identity, {
            connectionQuality: participant.connectionQuality,
        });

        info(
            'Track unsubscribed:',
            { track, publication, participant, activeCallID: activeCallDataRef?.current?.callID },
            'useLiveKitRoom',
            'track_unsubscribed'
        );

        if (participant.connectionQuality !== ConnectionQuality.Lost && participant.sid) return;

        // Remove audio element when track is unsubscribed
        if (track.kind === 'audio') {
            // Detach returns array of HTMLElements that were attached to this track
            const elements = track.detach();
            const container = ensureAudioContainer();

            elements.forEach((element) => {
                if (container?.contains(element)) {
                    container.removeChild(element);
                }
            });
            if (!elements.length) {
                container?.replaceChildren();
            }

            info(
                'Track audio unsubscribed:',
                { track, publication, participant, elements, activeCallID: activeCallDataRef?.current?.callID },
                'useLiveKitRoom',
                'track_unsubscribed'
            );

            setCallStatus(CallStatuses.READY);
            setTransferTarget(null);

            // Reset call related data
            setCallTranscript([]);
            setCallDetails(null);
            setAvailableStatuses([]);
            setPartyInfo(null);
            setQualificationsData([]);

            // Get call data from ref to avoid closure issues
            const currentCallData = activeCallDataRef.current;

            // Only update call status if call data exists
            // manual callback is handled on voice side so we don't need to update call status here
            if (currentCallData?.callID && !currentCallData.isManualCallback) {
                const response = await updateCallStatus({
                    callId: currentCallData.callID,
                    partyId: currentCallData.partyID,
                    callStatus: CallType.TRANSFER_COMPLETED,
                });
                info(
                    'Update call status response in track unsubscribed handler:',
                    { response, activeCallID: currentCallData?.callID },
                    'useLiveKitRoom',
                    'update_call_status_response'
                );
            } else {
                info(
                    'No active call data available for status update on track unsubscribe',
                    { currentCallData },
                    'useLiveKitRoom',
                    'no_active_call_data_available_for_status_update_on_track_unsubscribe'
                );
            }

            setShouldCloseCallConsole(true);
        }
    };

    const handleRoomMetadataChanged = (metadata: string) => {
        info(
            'Room metadata changed:',
            { metadata, activeCallID: activeCallDataRef?.current?.callID },
            'useLiveKitRoom',
            'room_metadata_changed'
        );
        try {
            // Parse room metadata to check for transfer
            // Unknows functionality which is not used anywhere in the codebase
            if (metadata) {
                const metadataParsed = JSON.parse(metadata);
                if (metadataParsed.transferTarget) {
                    setTransferTarget(metadataParsed.transferTarget);
                    setCallStatus(CallStatuses.TRANSFERRED);
                    console.log('Call transferred to:', metadataParsed.transferTarget);
                }
            }
        } catch (error) {
            console.error('Error parsing room metadata:', error);
        }
    };

    const handleAudioPlaybackStatusChanged = (room: Room) => () => {
        console.log('Audio playback status changed:', { canPlaybackAudio: room?.canPlaybackAudio });
        info(
            'Audio playback status changed:',
            { canPlaybackAudio: room?.canPlaybackAudio, activeCallID: activeCallDataRef?.current?.callID },
            'useLiveKitRoom',
            'audio_playback_status_changed'
        );
    };

    // Function to connect to LiveKit room
    const connectToLiveKitRoom = async (url: string, token: string): Promise<void> => {
        // Check if room reference exists
        if (livekitRoomRef.current) {
            warn('Disconnecting existing LiveKit room', {
                roomName: livekitRoomRef.current.name,
                roomState: livekitRoomRef.current.state,
                room: livekitRoomRef.current,
            });
            livekitRoomRef.current.disconnect();
            livekitRoomRef.current = null;
            setRoom(null);
        }

        console.log('Initializing LiveKit room');
        const newLivekitRoom = new Room({
            adaptiveStream: true,
            dynacast: true,
            reconnectPolicy,
        });

        setRoom(newLivekitRoom);
        livekitRoomRef.current = newLivekitRoom;

        // Set up event listeners for the room
        newLivekitRoom
            .on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged)
            .on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)
            .on(RoomEvent.ParticipantConnected, handleParticipantConnected)
            .on(RoomEvent.Disconnected, handleDisconnected)
            .on(RoomEvent.TrackSubscribed, handleTrackSubscribed)
            .on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)
            .on(RoomEvent.RoomMetadataChanged, handleRoomMetadataChanged)
            .on(RoomEvent.AudioPlaybackStatusChanged, handleAudioPlaybackStatusChanged(newLivekitRoom));

        try {
            await newLivekitRoom.connect(url, token);
            console.log('Connected to LiveKit room successfully');
            info(
                'Connected to LiveKit room successfully',
                {
                    room: newLivekitRoom,
                    activeCallID: activeCallDataRef?.current?.callID,
                },
                'useLiveKitRoom',
                'connected_to_livekit_room'
            );

            await newLivekitRoom.localParticipant.setMicrophoneEnabled(true);
            info(
                'Microphone enabled',
                {
                    localParticipant: newLivekitRoom.localParticipant,
                    remoteParticipants: newLivekitRoom.remoteParticipants,
                    activeCallID: activeCallDataRef?.current?.callID,
                },
                'useLiveKitRoom',
                'microphone_enabled'
            );
        } catch (error) {
            console.error('LiveKit connection failed:', error);
            setCallStatus(CallStatuses.READY);

            livekitRoomRef.current = null;
            setRoom(null);
            logError(
                'LiveKit connection failed:',
                error as Error,
                { room: newLivekitRoom, activeCallID: activeCallDataRef?.current?.callID },
                'useLiveKitRoom',
                'connection_failed'
            );
            throw error;
        }
    };

    return {
        room,
        livekitRoomRef,
        connectToLiveKitRoom,
    };
};
